// In development, REACT_APP_API_URL can point at a backend on another port
export const API_URL = process.env.NODE_ENV === 'production'
  ? 'https://api.theproducersfriend.com'
  : process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Thrown for any non-2xx response. `status` is the HTTP status and `data` is
// the parsed error body (or null if it wasn't JSON).
export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// The backend (FastAPI) sends `detail` as a string, an object, or a list of
// validation errors; the chat endpoints may send `error` instead.
const errorMessage = (data) => {
  const detail = data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(err => err.msg || JSON.stringify(err)).join(', ');
  }
  if (typeof detail === 'object' && detail !== null) {
    return detail.msg || JSON.stringify(detail);
  }
  return data?.error || 'An unexpected error occurred';
};

// When a streamed analysis ends without its result (a proxy cut it off, say)
const STREAM_CUT_OFF = 'The analysis stopped before it finished. Please try again.';

/**
 * fetch's shape over XMLHttpRequest, which (unlike fetch) reports how much of the body has been sent:
 * onUploadProgress gets (sent, total) in bytes. Aborts and network failures reject like fetch's do.
 *
 * A newline-delimited JSON response (an analysis's steps) is read as it arrives: onEvent gets each
 * event, and the response is its last one, { step: 'done', result } or { step: 'error', status, detail }.
 */
const fetchWithProgress = (url, { method = 'GET', headers = {}, body, signal, onUploadProgress, onEvent }) => (
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let read = 0;
    let last = null;
    const isStream = () => (xhr.getResponseHeader('Content-Type') || '').includes('ndjson');
    const readEvents = () => {
      const text = xhr.responseText;
      for (let end = text.indexOf('\n', read); end !== -1; end = text.indexOf('\n', read)) {
        const line = text.slice(read, end).trim();
        read = end + 1;
        if (!line) continue;
        const event = JSON.parse(line);
        if (event.step === 'done' || event.step === 'error') last = event;
        else onEvent?.(event);
      }
    };

    xhr.open(method, url);
    Object.entries(headers).forEach(([name, value]) => xhr.setRequestHeader(name, value));
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onUploadProgress?.(event.loaded, event.total);
    };
    xhr.onprogress = () => {
      if (isStream()) readEvents();
    };
    xhr.onload = () => {
      if (!isStream()) {
        resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, json: async () => JSON.parse(xhr.responseText) });
        return;
      }
      readEvents();
      if (last?.step === 'done') resolve({ ok: true, status: xhr.status, json: async () => last.result });
      else resolve({ ok: false, status: last?.status || 502, json: async () => ({ detail: last?.detail || STREAM_CUT_OFF }) });
    };
    xhr.onerror = () => reject(new TypeError('Failed to fetch'));
    xhr.onabort = () => reject(new DOMException('The request was aborted.', 'AbortError'));
    if (signal) {
      if (signal.aborted) return xhr.abort();
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(body);
  })
);

/**
 * Calls the backend and returns the parsed JSON body, throwing ApiError on a
 * non-2xx response. Network failures and aborts throw fetch's own errors.
 *
 * Options, on top of the usual fetch options (method, body, signal, ...):
 * - token: sent as a Bearer token
 * - json: sent as a JSON request body
 * - onUnauthorized: called when a request with a token gets a 401
 * - onUploadProgress: called with (sent, total) bytes as the body uploads
 * - onEvent: called with each event of a streamed (newline-delimited JSON) response
 *
 * Inside components, prefer `authFetch` from useAuth(), which fills in the
 * token and logs the user out on a 401.
 */
export const apiFetch = async (path, {
  token,
  json,
  onUnauthorized,
  onUploadProgress,
  onEvent,
  headers,
  ...options
} = {}) => {
  const requestHeaders = { ...headers };
  if (token) {
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }
  if (json !== undefined) {
    requestHeaders['Content-Type'] = 'application/json';
    options.body = JSON.stringify(json);
  }

  const response = onUploadProgress || onEvent
    ? await fetchWithProgress(`${API_URL}${path}`, { ...options, headers: requestHeaders, onUploadProgress, onEvent })
    : await fetch(`${API_URL}${path}`, { ...options, headers: requestHeaders });
  const data = await response.json().catch(() => null);

  if (!response.ok) {
    if (response.status === 401 && token && onUnauthorized) {
      onUnauthorized();
      throw new ApiError('Session expired. Please log in again.', 401, data);
    }
    throw new ApiError(errorMessage(data), response.status, data);
  }

  return data;
};
